import { useMemo } from "react";
import { Phone, Flame } from "lucide-react";

/**
 * SalesUeberblick — Skizze 2 aus dem Mockup:
 * 4 KPI-Kacheln (Heute fällig / Überfällig / Diese Woche / Neue Leads 24h)
 * + Liste "Heute fällig · direkt bearbeiten" mit Öffnen-Buttons.
 *
 * Rein clientseitig aus den bereits geladenen Pflegeeinrichtungen berechnet.
 */
const formatDe = (iso) => {
  if (!iso) return "";
  const s = String(iso).split("T")[0];
  const [y, m, d] = s.split("-");
  if (!y || !m || !d) return s;
  return `${d}.${m}.${y}`;
};

const kpiCardCls = "rounded-2xl px-6 py-5 flex flex-col justify-between min-h-[110px]";

const SalesUeberblick = ({ data = [], onOpenPE }) => {
  const {
    heuteFaellig,
    ueberfaellig,
    dieseWoche,
    neueLeads24h,
    heuteListe,
    neuhausNachfass,
  } = useMemo(() => {
    const heute = new Date();
    heute.setHours(0, 0, 0, 0);
    const heuteIso = heute.toISOString().split("T")[0];

    // Ende dieser Woche: Sonntag 23:59
    const wochenEnde = new Date(heute);
    const dow = (heute.getDay() + 6) % 7; // Mo=0..So=6
    wochenEnde.setDate(heute.getDate() + (6 - dow));
    wochenEnde.setHours(23, 59, 59, 999);

    // 24h Fenster
    const vor24h = new Date(heute.getTime() - 24 * 60 * 60 * 1000);

    const heuteListeArr = [];
    const neuhausArr = [];
    let cUeber = 0, cWoche = 0, cLead = 0;

    (data || []).forEach((pe) => {
      const wv = pe.naechste_wiedervorlage;
      if (wv) {
        // Vergleich als ISO-Date-String — sortierbar
        if (wv < heuteIso) cUeber += 1;
        else if (wv === heuteIso) heuteListeArr.push(pe);
        else {
          try {
            const d = new Date(wv);
            if (d <= wochenEnde) cWoche += 1;
          } catch { /* ignore */ }
        }
      }
      if (pe.created_at) {
        try {
          const c = new Date(pe.created_at);
          if (c >= vor24h) cLead += 1;
        } catch { /* ignore */ }
      }
      // Neuhaus-Kohorte: Nicht-Kunden, sortiert nach Erst-Eingang aufsteigend
      const isNeuhaus = pe.quelle === "Neuhaus" || (Array.isArray(pe.tags) && pe.tags.includes("Neuhaus"));
      const isKunde = pe.akquise_status === "kunde" || (Array.isArray(pe.tags) && pe.tags.includes("Neuhaus-Kunde"));
      if (isNeuhaus && !isKunde) {
        neuhausArr.push(pe);
      }
    });

    // Neuhaus-Nachfass sortiert nach Erst-Eingang aufsteigend (älteste zuerst = am dringendsten)
    neuhausArr.sort((a, b) => {
      const ea = a.neuhaus_erster_eingang || a.created_at || "";
      const eb = b.neuhaus_erster_eingang || b.created_at || "";
      return ea.localeCompare(eb);
    });

    return {
      heuteFaellig: heuteListeArr.length,
      ueberfaellig: cUeber,
      dieseWoche: cWoche,
      neueLeads24h: cLead,
      heuteListe: heuteListeArr,
      neuhausNachfass: neuhausArr,
    };
  }, [data]);

  return (
    <div className="bg-[#F7F5F2] border border-[#efe4d1] rounded-2xl p-6 md:p-7 mb-5" data-testid="sales-ueberblick">
      <h2 className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#9A3412] mb-4">
        Wer heute fällig, überfällig, diese Woche
      </h2>

      {/* 4 KPI-Kacheln */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <div className={`${kpiCardCls} bg-[#9A3412] text-[#FBEDE7]`} data-testid="kpi-heute">
          <div className="text-4xl font-semibold tabular-nums leading-none">{heuteFaellig}</div>
          <div className="text-sm mt-2">Heute fällig</div>
        </div>
        <div className={`${kpiCardCls} bg-[#c26a1f] text-white`} data-testid="kpi-ueberfaellig">
          <div className="text-4xl font-semibold tabular-nums leading-none">{ueberfaellig}</div>
          <div className="text-sm mt-2">Überfällig</div>
        </div>
        <div className={`${kpiCardCls} bg-[#F6E3DA] text-[#5a4a35]`} data-testid="kpi-woche">
          <div className="text-4xl font-semibold tabular-nums leading-none">{dieseWoche}</div>
          <div className="text-sm mt-2">Diese Woche</div>
        </div>
        <div className={`${kpiCardCls} bg-[#2a2118] text-[#FBEDE7]`} data-testid="kpi-neue-leads">
          <div className="text-4xl font-semibold tabular-nums leading-none">{neueLeads24h}</div>
          <div className="text-sm mt-2">Neue Leads (24h)</div>
        </div>
      </div>

      {/* Heute-fällig-Liste */}
      <div className="bg-white border border-[#F6E3DA] rounded-xl p-4">
        <h3 className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#9A3412] mb-3">
          Heute fällig — direkt bearbeiten
        </h3>
        {heuteListe.length === 0 ? (
          <p className="text-sm text-[#78716c] italic py-1" data-testid="heute-empty">
            Heute steht nichts auf der Wiedervorlage-Liste. Zeit für einen Kaffee 
          </p>
        ) : (
          <div className="divide-y divide-[#F6E3DA]">
            {heuteListe.slice(0, 10).map((pe) => {
              const apName = pe.ansprechpartner_name || "";
              const tel = pe.ansprechpartner_telefon || pe.telefon || "";
              const grund = pe.wiedervorlage_grund || "";
              return (
                <div
                  key={pe.id}
                  className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-4 py-2.5 first:pt-0 last:pb-0 text-sm"
                  data-testid={`heute-row-${pe.id}`}
                >
                  <div className="min-w-0">
                    <span className="text-[#2a2118] font-medium">{pe.einrichtungsname}</span>
                    {apName && <span className="text-[#78716c]"> · {apName}</span>}
                  </div>
                  <div className="text-xs text-[#9A3412] whitespace-nowrap">
                    {grund || (pe.akquise_status ? pe.akquise_status.replace(/_/g, " ") : "Wiedervorlage")}
                  </div>
                  <div className="text-xs text-[#78716c] whitespace-nowrap tabular-nums flex items-center gap-1">
                    {tel && <><Phone size={11} />{tel}</>}
                  </div>
                  <button
                    onClick={() => onOpenPE && onOpenPE(pe.id)}
                    className="px-4 py-1.5 bg-[#9A3412] text-white text-xs font-medium rounded-md hover:bg-[#5a3d1a] transition"
                    data-testid={`heute-open-${pe.id}`}
                  >
                    Öffnen
                  </button>
                </div>
              );
            })}
            {heuteListe.length > 10 && (
              <p className="text-xs text-[#78716c] italic pt-2">
                … und {heuteListe.length - 10} weitere
              </p>
            )}
          </div>
        )}
      </div>

      {/* Neuhaus-Kohorte — Nachfaß-Liste */}
      {neuhausNachfass.length > 0 && (
        <div className="bg-white border border-[#F6E3DA] rounded-xl p-4 mt-3" data-testid="neuhaus-nachfass-card">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#9A3412] flex items-center gap-1.5">
              <Flame size={12} className="text-[#c26a1f]" />
              Neuhaus-Kohorte — noch nicht als Kunde gewonnen ({neuhausNachfass.length})
            </h3>
            <span className="text-[11px] text-[#78716c] italic">älteste zuerst · direkt nachhören</span>
          </div>
          <div className="divide-y divide-[#F6E3DA]">
            {neuhausNachfass.slice(0, 12).map((pe) => {
              const apName = pe.ansprechpartner_name || "";
              const tel = pe.ansprechpartner_telefon || pe.telefon || "";
              const eingang = pe.neuhaus_erster_eingang || "";
              const anfrage = pe.anfrage_fuer || "";
              const bereitsKontaktiert = !!pe.letzter_kontakt_am && pe.letzter_kontakt_am !== pe.neuhaus_letzter_eingang;
              return (
                <div
                  key={pe.id}
                  className="grid grid-cols-[1fr_auto_auto_auto_auto] items-center gap-4 py-2.5 first:pt-0 last:pb-0 text-sm"
                  data-testid={`neuhaus-row-${pe.id}`}
                >
                  <div className="min-w-0">
                    <span className="text-[#2a2118] font-medium">{pe.einrichtungsname}</span>
                    {apName && <span className="text-[#78716c]"> · {apName}</span>}
                  </div>
                  <div className="text-xs text-[#9A3412] whitespace-nowrap">
                    {anfrage || "—"}
                  </div>
                  <div className="text-xs text-[#78716c] whitespace-nowrap tabular-nums">
                    seit {formatDe(eingang)}
                  </div>
                  <div className="text-xs whitespace-nowrap flex items-center gap-1">
                    {bereitsKontaktiert ? (
                      <span className="text-[#4a6b2b]">✓ bereits kontaktiert</span>
                    ) : (
                      <span className="text-[#c26a1f] font-medium">nachfassen</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {tel && (
                      <a
                        href={`tel:${tel}`}
                        className="text-xs text-[#9A3412] hover:text-[#5a3d1a] whitespace-nowrap flex items-center gap-1"
                        title={tel}
                        data-testid={`neuhaus-tel-${pe.id}`}
                      >
                        <Phone size={11} />
                        {tel}
                      </a>
                    )}
                    <button
                      onClick={() => onOpenPE && onOpenPE(pe.id)}
                      className="px-3 py-1 bg-[#2a2118] text-white text-xs font-medium rounded-md hover:bg-black transition"
                      data-testid={`neuhaus-open-${pe.id}`}
                    >
                      Öffnen
                    </button>
                  </div>
                </div>
              );
            })}
            {neuhausNachfass.length > 12 && (
              <p className="text-xs text-[#78716c] italic pt-2">
                … und {neuhausNachfass.length - 12} weitere — nutze den Tag-Filter &bdquo;Neuhaus&ldquo; um die volle Liste zu sehen
              </p>
            )}
          </div>
        </div>
      )}

      {/* Fußnote wie im Mockup */}
      <p className="text-xs text-[#78716c] italic mt-3">
        Wiedervorlage-Kalender {formatDe(new Date().toISOString())} — die 4 Kacheln geben dir jederzeit das Bauchgefühl, ohne suchen zu müssen.
      </p>
    </div>
  );
};

export default SalesUeberblick;
